import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useUserGroups() {
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }

    const { data: memberships } = await supabase
      .from("group_members")
      .select("group_id")
      .eq("user_id", user.id);

    if (!memberships || memberships.length === 0) {
      setUserGroups([]);
      setLoading(false);
      return;
    }

    const groupIds = memberships.map((m) => m.group_id);
    const { data: groups } = await supabase
      .from("groups")
      .select("*")
      .in("id", groupIds);

    setUserGroups(groups || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { userGroups, loading, refetch: fetch, hasGroup: userGroups.length > 0 };
}

export function useAllGroups() {
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("groups")
      .select("*")
      .order("created_at", { ascending: false });

    if (!error) setGroups(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { groups, loading, refetch: fetch };
}

export function useJoinRequests(groupId?: string) {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRequests = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    const { data } = await supabase
      .from("group_join_requests" as any)
      .select("*")
      .eq("group_id", groupId)
      .eq("status", "pending");
    setRequests((data as any) || []);
    setLoading(false);
  }, [groupId]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const approveRequest = async (requestId: string, userId: string) => {
    // Add as member
    const { error: memberError } = await supabase
      .from("group_members")
      .insert({ group_id: groupId!, user_id: userId, role: "miembro" } as any);

    if (memberError) {
      toast.error("Error añadiendo miembro");
      console.error(memberError);
      return;
    }

    // Update request status
    await supabase
      .from("group_join_requests" as any)
      .update({ status: "approved", reviewed_at: new Date().toISOString() } as any)
      .eq("id", requestId);

    toast.success("Solicitud aprobada");
    await fetchRequests();
  };

  const rejectRequest = async (requestId: string) => {
    await supabase
      .from("group_join_requests" as any)
      .update({ status: "rejected", reviewed_at: new Date().toISOString() } as any)
      .eq("id", requestId);

    toast.info("Solicitud rechazada");
    await fetchRequests();
  };

  return { requests, loading, approveRequest, rejectRequest, refetch: fetchRequests };
}

export function useMyJoinRequests() {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("group_join_requests" as any)
      .select("*");
    setRequests((data as any) || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const sendJoinRequest = async (groupId: string, message?: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Debes iniciar sesión"); return; }

    const { error } = await supabase
      .from("group_join_requests" as any)
      .insert({ group_id: groupId, user_id: user.id, message: message || null } as any);

    if (error) {
      if (error.message.includes("duplicate") || error.message.includes("unique")) {
        toast.info("Ya has enviado una solicitud a este grupo");
      } else {
        toast.error("Error enviando solicitud");
        console.error(error);
      }
      return;
    }

    toast.success("¡Solicitud enviada! El admin del grupo la revisará.");
    await fetch();
  };

  const cancelRequest = async (requestId: string) => {
    await supabase
      .from("group_join_requests" as any)
      .delete()
      .eq("id", requestId);
    toast.info("Solicitud cancelada");
    await fetch();
  };

  return { requests, loading, sendJoinRequest, cancelRequest, refetch: fetch };
}
